import type { IUser } from "../contexts/Users/types";

interface SlackNotifyOptions {
    message: string;
    error?: any;
    context?: Record<string, any>;
    user?: IUser | null;
    level?: "info" | "warn" | "error" | "fatal";
}

class SlackLogger {
    private webhookUrl: string;
    private lastSentMessages: Map<string, number> = new Map();
    private readonly THROTTLE_TIME = 1000 * 60 * 5; // 5 minutes

    constructor() {
        this.webhookUrl = import.meta.env.VITE_SLACK_WEBHOOK_URL || "";
    }

    private serializeError(error: any): string {
        if (error instanceof Error) {
            return `Message: ${error.message}\nStack: ${error.stack}`;
        }
        if (typeof error === "object") {
            try {
                return JSON.stringify(error, null, 2);
            } catch (e) {
                return String(error);
            }
        }
        return String(error);
    }

    async notify({ message, error, context, user, level = "error" }: SlackNotifyOptions) {
        if (!this.webhookUrl) {
            console.warn("Slack Webhook URL not configured. Skipping notification.");
            return;
        }

        const fingerprint = `${message}-${level}`;
        const now = Date.now();
        const lastSent = this.lastSentMessages.get(fingerprint);

        if (lastSent && now - lastSent < this.THROTTLE_TIME) {
            return;
        }

        this.lastSentMessages.set(fingerprint, now);

        const levelEmoji = {
            info: "ℹ️",
            warn: "⚠️",
            error: "❌",
            fatal: "🚨",
        };

        const blocks: any[] = [
            {
                type: "header",
                text: {
                    type: "plain_text",
                    text: `${levelEmoji[level]} Frontend Error Alert`,
                    emoji: true,
                },
            },
            {
                type: "section",
                text: {
                    type: "mrkdwn",
                    text: `*Message:* ${message}`,
                },
            },
            {
                type: "section",
                fields: [
                    {
                        type: "mrkdwn",
                        text: `*URL:* ${window.location.href}`,
                    },
                    {
                        type: "mrkdwn",
                        text: `*Environment:* ${import.meta.env.MODE}`,
                    },
                ],
            },
        ];

        if (user) {
            blocks.push({
                type: "section",
                text: {
                    type: "mrkdwn",
                    text: `*User Information:*\n• *ID:* ${user.id}\n• *Name:* ${user.name}\n• *Email:* ${user.email}\n• *Status:* ${user.status === 1 ? "Active" : "Inactive"} (Role: ${user.role_id})`,
                },
            });
        }

        if (context) {
            blocks.push({
                type: "section",
                text: {
                    type: "mrkdwn",
                    text: `*Context:* \`\`\`${JSON.stringify(context, null, 2)}\`\`\``,
                },
            });
        }

        if (error) {
            const errorDetail = this.serializeError(error);
            // Slack limits block text to 3000 chars, so we truncate if needed
            const truncatedError = errorDetail.length > 2900
                ? errorDetail.substring(0, 2900) + "... [Truncated]"
                : errorDetail;

            blocks.push({
                type: "section",
                text: {
                    type: "mrkdwn",
                    text: `*Full Error Detail:* \`\`\`${truncatedError}\`\`\``,
                },
            });
        }

        const payload = { blocks };

        try {
            await fetch(this.webhookUrl, {
                method: "POST",
                mode: "no-cors",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
        } catch (err) {
            console.error("Failed to send notification to Slack:", err);
        }
    }
}

export const slackLogger = new SlackLogger();
